import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Button, Form, InputGroup, Alert,
  Spinner, Badge, Tab, Tabs
} from 'react-bootstrap';
import {
  Shirt, Plus, Search, RefreshCw, X, Scissors, AlertTriangle,
  Clock, ArrowLeft
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import customOrderService from '../../../services/customOrderService';
import OrderCard from './OrderCard';
import CreateOrderModal from './CreateOrderModal';
import OrderDetailModal from './OrderDetailModal';
import { useAuth } from '../../../contexts/AuthContext';
import { useBoutique } from '../../../contexts/BoutiqueContext';

import OrderTicketPrintService from '../../../services/OrderTicketPrintService';

/**
 * ✅ ATELIER: Page principale de gestion des commandes atelier.
 */
const CustomOrdersList = () => {

  const { user } = useAuth();
  const { isAtelierEnabled } = useBoutique();
  const isManager = user?.role === 'MANAGER';
  const navigate = useNavigate(); // ✅ AJOUT

  // États principaux
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Filtres
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Stats par statut (compteurs)
  const [statusCounts, setStatusCounts] = useState({
    all: 0,
    active: 0,
    overdue: 0,
    upcoming: 0
  });

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const { currentBoutique } = useBoutique();
  const [printingOrderId, setPrintingOrderId] = useState(null);

  // Chargement
  useEffect(() => {
    if (isAtelierEnabled) {
      loadOrders();
    }
  }, [isAtelierEnabled, activeTab]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError(null);

      let response;
      const searchParam = searchQuery.trim() || null;

      switch (activeTab) {
        case 'active':
          response = await customOrderService.getActiveOrders();
          break;
        case 'overdue':
          response = await customOrderService.getOverdueOrders();
          break;
        case 'upcoming':
          response = await customOrderService.getUpcomingDeliveries(7);
          break;
        case 'delivered':
          response = await customOrderService.getOrders({
            page: 0, size: 50, status: 'DELIVERED', search: searchParam
          });
          response = { success: response.success, data: response.data?.content || [] };
          break;
        case 'cancelled':
          response = await customOrderService.getOrders({
            page: 0, size: 50, status: 'CANCELLED', search: searchParam
          });
          response = { success: response.success, data: response.data?.content || [] };
          break;
        default: // 'all'
          response = await customOrderService.getOrders({
            page: 0, size: 50, search: searchParam
          });
          response = { success: response.success, data: response.data?.content || [] };
      }

      if (response.success) {
        const list = Array.isArray(response.data) ? response.data : [];
        setOrders(list);
      } else {
        setError(response.message || 'Erreur lors du chargement');
      }

    } catch (err) {
      console.error('❌ Erreur chargement commandes:', err);
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  // Charger compteurs en arrière-plan
  useEffect(() => {
    if (isAtelierEnabled) {
      loadCounts();
    }
  }, [isAtelierEnabled, orders]);

  const loadCounts = async () => {
    try {
      const [allRes, activeRes, overdueRes, upcomingRes] = await Promise.all([
        customOrderService.getOrders({ page: 0, size: 1 }),
        customOrderService.getActiveOrders(),
        customOrderService.getOverdueOrders(),
        customOrderService.getUpcomingDeliveries(7)
      ]);

      setStatusCounts({
        all: allRes.success ? (allRes.data?.totalElements || 0) : 0,
        active: activeRes.success ? activeRes.data.length : 0,
        overdue: overdueRes.success ? overdueRes.data.length : 0,
        upcoming: upcomingRes.success ? upcomingRes.data.length : 0
      });
    } catch (err) {
      console.error('Erreur compteurs:', err);
    }
  };

  // Filtrage local par recherche
  const filteredOrders = searchQuery.trim()
    ? orders.filter(o => {
        const q = searchQuery.trim().toLowerCase();
        return (
          o.orderNumber.toLowerCase().includes(q) ||
          o.customerName.toLowerCase().includes(q) ||
          (o.customerPhone && o.customerPhone.includes(q))
        );
      })
    : orders;

  // ===== HANDLERS =====

  const handleCreateClick = () => {
    setShowCreateModal(true);
  };

  const handleCreateSuccess = (newOrder) => {
    setSuccess(`Commande ${newOrder.orderNumber} créée avec succès`);
    setTimeout(() => setSuccess(null), 3000);
    loadOrders();
  };

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setShowDetailModal(true);
  };

  const handleOrderUpdated = (updatedOrder) => {
    // Ne pas écraser la commande affichée si l'appelant n'envoie rien
    if (updatedOrder) {
      setSelectedOrder(updatedOrder);
    }
    loadOrders();
  };

  /**
 * Préparer les infos boutique pour l'impression
 */
const getBoutiqueInfoForPrint = () => ({
  name: currentBoutique?.name || 'Ma Boutique',
  address: currentBoutique?.address || 'Dakar, Sénégal',
  phone: currentBoutique?.phone || '+221 XX XXX XX XX',
  phone1: currentBoutique?.phone1 || '',
  email: currentBoutique?.email || null,
  logoUrl: currentBoutique?.logoUrl || currentBoutique?.logo,
  logo: currentBoutique?.logo || currentBoutique?.logoUrl,
  primaryColor: currentBoutique?.primaryColor || '#3b82f6',
  secondaryColor: currentBoutique?.secondaryColor || '#1d4ed8'
});

/**
 * Aperçu du ticket d'une commande
 */
const handlePreviewOrderTicket = async (order) => {
  try {
    setError(null);
    const result = await OrderTicketPrintService.previewOrderTicket(
      order,
      getBoutiqueInfoForPrint(),
      { name: order.sellerName || 'Vendeur', id: order.sellerId }
    );

    if (!result.success) {
      setError(result.message);
    }
  } catch (err) {
    setError(err.message || 'Erreur lors de l\'aperçu');
  }
};

/**
 * Impression du ticket d'une commande
 */
const handlePrintOrderTicket = async (order) => {
  try {
    setPrintingOrderId(order.id);
    setError(null);

    const result = await OrderTicketPrintService.printOrderTicket(
      order,
      getBoutiqueInfoForPrint(),
      { name: order.sellerName || 'Vendeur', id: order.sellerId }
    );

    if (result.success) {
      setSuccess?.(`Ticket ${order.orderNumber} envoyé à l'impression`);
      setTimeout(() => setSuccess?.(null), 3000);
    } else {
      setError(result.message);
    }
  } catch (err) {
    setError(err.message || 'Erreur lors de l\'impression');
  } finally {
    setPrintingOrderId(null);
  }
};

  // Si atelier désactivé
  if (!isAtelierEnabled) {
    return (
      <Container fluid className="py-4">
        <Card className="text-center py-5">
          <Card.Body>
            <Scissors size={64} className="text-muted mb-3" />
            <h4>Module Atelier non activé</h4>
            <p className="text-muted">
              Le module Atelier n'est pas activé pour cette boutique.
            </p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* En-tête */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              {/* ✅ Bouton retour au dashboard atelier */}
<Button
  variant="link"
  onClick={() => navigate('/atelier')}
  className="p-0 mb-2 text-decoration-none d-flex align-items-center gap-1"
>
  <ArrowLeft size={16} />
  <small>Retour au tableau de bord Atelier</small>
</Button>
              <h1 className="h3 mb-1 d-flex align-items-center gap-2">
                <Shirt size={28} className="text-primary" />
                Commandes Atelier
              </h1>
              <p className="text-muted mb-0">
                Suivi et gestion des commandes de couture
              </p>
            </div>

            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                onClick={loadOrders}
                disabled={loading}
              >
                <RefreshCw size={16} className={loading ? 'spin' : ''} />
              </Button>

              <Button variant="primary" onClick={handleCreateClick}>
                <Plus size={16} className="me-1" />
                Nouvelle commande
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {/* Alertes */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Stats rapides */}
      <Row className="g-3 mb-3">
        <Col xs={6} md={3}>
          <Card className="stat-card border-primary border-start border-4 h-100">
            <Card.Body className="py-2">
              <div className="small text-muted">Total</div>
              <div className="h4 mb-0">{statusCounts.all}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="stat-card border-info border-start border-4 h-100">
            <Card.Body className="py-2">
              <div className="small text-muted">En cours</div>
              <div className="h4 mb-0 text-info">{statusCounts.active}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="stat-card border-warning border-start border-4 h-100">
            <Card.Body className="py-2">
              <div className="small text-muted d-flex align-items-center gap-1">
                <Clock size={12} />
                Livraison ≤ 7j
              </div>
              <div className="h4 mb-0 text-warning">{statusCounts.upcoming}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="stat-card border-danger border-start border-4 h-100">
            <Card.Body className="py-2">
              <div className="small text-muted d-flex align-items-center gap-1">
                <AlertTriangle size={12} />
                En retard
              </div>
              <div className="h4 mb-0 text-danger">{statusCounts.overdue}</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filtres + Recherche */}
      <Card className="mb-3 shadow-sm">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={8}>
              <Form.Label className="small text-muted">Rechercher</Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <Search size={16} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Numéro, nom client, téléphone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => setSearchQuery('')}
                  >
                    <X size={16} />
                  </Button>
                )}
              </InputGroup>
            </Col>
            <Col md={4} className="text-md-end">
              <div className="small text-muted">
                <strong>{filteredOrders.length}</strong>{' '}
                commande{filteredOrders.length > 1 ? 's' : ''} affichée{filteredOrders.length > 1 ? 's' : ''}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Onglets de filtrage */}
      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-3"
        fill
      >
        <Tab eventKey="all" title={
          <span>Toutes <Badge bg="secondary" pill>{statusCounts.all}</Badge></span>
        } />
        <Tab eventKey="active" title={
          <span>En cours <Badge bg="info" pill>{statusCounts.active}</Badge></span>
        } />
        <Tab eventKey="upcoming" title={
          <span>Livraisons proches <Badge bg="warning" text="dark" pill>{statusCounts.upcoming}</Badge></span>
        } />
        <Tab eventKey="overdue" title={
          <span>En retard <Badge bg="danger" pill>{statusCounts.overdue}</Badge></span>
        } />
        <Tab eventKey="delivered" title="Livrées" />
        <Tab eventKey="cancelled" title="Annulées" />
      </Tabs>

      {/* Contenu */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <div className="mt-2 text-muted">Chargement des commandes...</div>
        </div>
      ) : filteredOrders.length === 0 ? (
        <Card className="text-center py-5 shadow-sm">
          <Card.Body>
            <Shirt size={64} className="text-muted mb-3" />
            <h5>Aucune commande trouvée</h5>
            <p className="text-muted mb-3">
              {searchQuery
                ? 'Aucune commande ne correspond à votre recherche.'
                : activeTab === 'all'
                  ? 'Aucune commande n\'a encore été créée.'
                  : 'Aucune commande dans cette catégorie.'}
            </p>
            {activeTab === 'all' && !searchQuery && (
              <Button variant="primary" onClick={handleCreateClick}>
                <Plus size={16} className="me-1" />
                Créer la première commande
              </Button>
            )}
          </Card.Body>
        </Card>
      ) : (
        <Row>
          {filteredOrders.map(order => (
            <Col
              key={order.id}
              xl={4} lg={6} md={6} sm={12}
              className="mb-3"
            >
              <OrderCard
    key={order.id}
    order={order}
    onClick={() => handleOrderClick(order)}
    onPreview={handlePreviewOrderTicket}
    onPrint={handlePrintOrderTicket}
    printing={printingOrderId}
  />
            </Col>
          ))}
        </Row>
      )}

      {/* Modal création */}
      <CreateOrderModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
      />

      {/* Modal détail */}
      <OrderDetailModal
        show={showDetailModal}
        onHide={() => {
          setShowDetailModal(false);
          setSelectedOrder(null);
        }}
        order={selectedOrder}
        onOrderUpdated={handleOrderUpdated}
        isManager={isManager}
      />

      <style>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .stat-card {
          border-top: none !important;
          border-right: none !important;
          border-bottom: none !important;
        }
      `}</style>
    </Container>
  );
};

export default CustomOrdersList;